import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import TabWrapper from './tab-wrapper'

import style from './tab-bar.module.css'

const tabBarPropTypes = {
    className: PropTypes.string,
    onTabClick: PropTypes.func.isRequired,
    selectedTabId: PropTypes.string,
    tabHighlightingProperty: PropTypes.string,
    tabIdProperty: PropTypes.string
}

const tabBarDefaultProps = {
    tabHighlightingProperty: 'isHighlighted',
    tabIdProperty: 'id'
}

/**
 * This is a tab bar which contains only the tabs (not also the corresponding tab content).
 * These tabs shall be given as children to the bar. The only task of a tab children is its rendering.
 *
 * A tab has to define an ID property and a highlighting property.
 * - the ID property can be of any type and is used as a reference to a tab.
 * - the highlighting property is of boolean type. A value of 'true' means that the tab is selected and therefore
 * it should be render itself in an other way as in the normal rendering.
 *
 * The selection of a tab is handled by the tab bar. If a selection occurrs a given callback function will be called
 * (with the selected tab ID as argument).
 * @example
 * const func = (id) => { console.log('Tab Id:', id) }
 *
 * @property {Function} propTypes.onTabClick                                - callback function.
 * @property {Array|string} propTypes.children                              - array of React elements (might be empty)
 * @property {string} [propTypes.tabIdProperty='id']                        - name of the ID property
 * @property {string} [propTypes.tabHighlightingProperty='isHighlighted']   - name of the highlighting property
 * @property {string} propTypes.selectedTabId                               - the Id of the selected tab
 * @property {string} [propTypes.className]                                 - CSS class name for additional styling
 *
 * @deprecated Please use the TabBar of the library @wf-common/molecules.
 */
class TabBar extends Component {
    constructor(props) {
        super(props)
        this.state = {
            selectedTabId: props.selectedTabId || null
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // HANDS OFF! This has to be stay as it is or you know what you are doing!
        if (nextProps.selectedTabId) {
            this.setState({ selectedTabId: nextProps.selectedTabId })
        }
    }

    render() {
        const { children: tabs, className, tabHighlightingProperty, tabIdProperty } = this.props

        if (React.Children.count(tabs) === 0) {
            return null
        }

        // The initial state is null, but we need to select at least one tab, so as default select the first tab.
        const selectedTabId = this.state.selectedTabId || React.Children.toArray(tabs)[0].props[tabIdProperty]

        return (
            <div className={cx(style['tab-bar'], className, 't3sel-tab-bar')}>
                {React.Children.map(tabs, tab => {
                    if (!tab) {
                        return null
                    }

                    const additionalTabProperties = {
                        [tabHighlightingProperty]: tab.props[tabIdProperty] === selectedTabId
                    }
                    const onClick = this.select(tab.props[tabIdProperty])

                    return <TabWrapper {...{ additionalTabProperties, onClick }}>{tab}</TabWrapper>
                })}
            </div>
        )
    }

    select(tabId) {
        return () => {
            this.props.onTabClick(tabId)
            this.setState({
                selectedTabId: tabId
            })
        }
    }
}

TabBar.propTypes = tabBarPropTypes
TabBar.defaultProps = tabBarDefaultProps

export default TabBar
